import { useState } from "react";
import useSWR from "swr";
import { ChevronDown, Layers3, ToggleLeft, ToggleRight } from "lucide-react";
import { apiFetch, swrFetcher, type AdminModel } from "@/lib/api";
import { THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const PROVIDER_DOTS: Record<string, string> = {
  google: "bg-sky-400",
  openai: "bg-emerald-400",
  fal: "bg-fuchsia-400",
  byteplus_ark: "bg-rose-400",
  topaz: "bg-amber-400",
  elevenlabs: "bg-violet-400",
};

interface ProviderGroup {
  key: string;
  name: string;
  active: boolean;
  models: AdminModel[];
  modalities: string[];
  activeModelCount: number;
}

export function ModelsTab() {
  const { data: models, isLoading, mutate } = useSWR<AdminModel[]>("/api/admin/models", swrFetcher);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());

  const toggleActive = async (m: AdminModel) => {
    try {
      await apiFetch(`/api/admin/models/${m.id}`, { method: "PATCH", body: JSON.stringify({ active: !m.active }) });
      await mutate();
    } catch (cause) {
      // Previously an unhandled rejection: the toggle silently did nothing.
      console.error(cause);
      window.alert(cause instanceof Error ? cause.message : "Update failed");
    }
  };

  if (isLoading || !models) return <p className="text-sm text-studio-muted">Loading models…</p>;

  const providerGroups = Array.from(
    models.reduce<Map<string, AdminModel[]>>((groups, model) => {
      const providerModels = groups.get(model.provider) ?? [];
      providerModels.push(model);
      groups.set(model.provider, providerModels);
      return groups;
    }, new Map())
  )
    .map<ProviderGroup>(([key, providerModels]) => ({
      key,
      name: providerModels[0].provider_name,
      active: providerModels[0].provider_active,
      models: [...providerModels].sort((a, b) => a.display_name.localeCompare(b.display_name)),
      modalities: Array.from(new Set(providerModels.flatMap((model) => model.modalities))).sort(),
      activeModelCount: providerModels.filter((model) => model.active).length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const allExpanded = providerGroups.length > 0 && expandedProviders.size === providerGroups.length;
  const activeModelCount = models.filter((model) => model.active).length;

  function toggleProvider(providerKey: string) {
    setExpandedProviders((current) => {
      const next = new Set(current);
      if (next.has(providerKey)) next.delete(providerKey);
      else next.add(providerKey);
      return next;
    });
  }

  function toggleAllProviders() {
    setExpandedProviders(
      allExpanded ? new Set() : new Set(providerGroups.map((provider) => provider.key))
    );
  }

  return (
    <div className="min-w-0">
      <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-studio-accent">Model registry</p>
          <h1 className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">Models</h1>
          <p className="mt-1.5 text-sm text-studio-muted">
            {models.length} models across {providerGroups.length} providers
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="success">{activeModelCount} active</Badge>
          {activeModelCount < models.length && (
            <Badge variant="neutral">{models.length - activeModelCount} disabled</Badge>
          )}
          <button
            type="button"
            onClick={toggleAllProviders}
            className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-studio-border bg-studio-panel px-3 text-xs font-semibold text-studio-muted transition-colors hover:border-studio-border-light hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
          >
            <Layers3 className="h-3.5 w-3.5" />
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {providerGroups.map((provider) => {
          const expanded = expandedProviders.has(provider.key);
          const panelId = `provider-models-${provider.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
          return (
            <section
              key={provider.key}
              className={cn(
                "overflow-hidden rounded-xl border bg-studio-panel transition-colors",
                expanded ? "border-studio-border-light shadow-panel" : "border-studio-border"
              )}
            >
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => toggleProvider(provider.key)}
                className="flex w-full min-w-0 flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-studio-card/40 focus-visible:outline-none focus-visible:shadow-glow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-studio-border bg-studio-card font-display text-sm font-bold text-studio-ink">
                    {provider.name.slice(0, 1).toUpperCase()}
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-studio-panel",
                        PROVIDER_DOTS[provider.key] ?? "bg-studio-muted"
                      )}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-studio-ink">{provider.name}</span>
                      {!provider.active && <Badge variant="danger">Provider disabled</Badge>}
                    </span>
                    <span className="mt-1 block font-mono text-[10px] text-studio-faint">{provider.key}</span>
                  </span>
                </span>

                <span className="flex w-full flex-wrap items-center gap-2 pl-[3.25rem] sm:w-auto sm:justify-end sm:pl-0">
                  {provider.modalities.map((modality) => (
                    <Badge key={modality} variant="neutral">{modality}</Badge>
                  ))}
                  <span className="text-xs font-medium text-studio-muted">
                    {provider.activeModelCount}/{provider.models.length} active
                  </span>
                  <ChevronDown
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0 text-studio-muted transition-transform sm:ml-1",
                      expanded && "rotate-180"
                    )}
                  />
                </span>
              </button>

              {expanded && (
                <div id={panelId} className="max-w-full overflow-x-auto border-t border-studio-border">
                  <table className="w-full min-w-[640px] text-sm">
                    <THead className="bg-studio-card/60">
                      <tr>
                        <Th>Model</Th>
                        <Th>Modalities</Th>
                        <Th>Status</Th>
                        <Th className="text-right">Toggle</Th>
                      </tr>
                    </THead>
                    <TBody>
                      {provider.models.map((model) => (
                        <Tr key={model.id}>
                          <Td>
                            <p className="font-medium text-studio-ink">{model.display_name}</p>
                            <p className="font-mono text-xs text-studio-faint">{model.key} · {model.family}</p>
                          </Td>
                          <Td>
                            <div className="flex flex-wrap gap-1">
                              {model.modalities.map((modality) => (
                                <Badge key={modality} variant="neutral">{modality}</Badge>
                              ))}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              <Badge variant={model.active ? "success" : "danger"}>
                                {model.active ? "Active" : "Disabled"}
                              </Badge>
                              {model.deprecated_since && <Badge variant="warning">deprecated</Badge>}
                            </div>
                          </Td>
                          <Td className="text-right">
                            <button
                              type="button"
                              aria-label={`${model.active ? "Disable" : "Enable"} ${model.display_name}`}
                              aria-pressed={model.active}
                              onClick={() => void toggleActive(model)}
                              className={cn(
                                "inline-flex h-11 w-11 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm sm:h-9 sm:w-9",
                                model.active
                                  ? "text-studio-success hover:bg-studio-success/10"
                                  : "text-studio-danger hover:bg-studio-danger/10"
                              )}
                            >
                              {model.active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                            </button>
                          </Td>
                        </Tr>
                      ))}
                    </TBody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
